import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex flex-col items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">内容未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您访问的页面不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: `https://sigua.video/article/${article.slug}`,
    datePublished: `2024-01-15`,
    dateModified: article.updateDate,
    author: { '@type': 'Person', name: '丝瓜视频编辑' },
    publisher: { '@type': 'Organization', name: '丝瓜视频', url: 'https://sigua.video' },
    image: article.coverImage || undefined,
    about: {
      '@type': 'Thing',
      name: categoryInfo?.name || article.category,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryInfo?.name || article.category, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-4 md:py-6">
          <div className="max-w-4xl mx-auto">
            <header className="mb-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {categoryInfo && (
                  <Link
                    to={`/category/${categoryInfo.slug}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${categoryInfo.icon} mr-1`}></i>
                    {categoryInfo.name}
                  </Link>
                )}
                {article.isHot && (
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                    热门推荐
                  </span>
                )}
                {article.isNew && (
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    新上线
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-800">{article.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.year}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-map-pin-line"></i>
                  {article.region}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.duration}
                </span>
              </div>
            </header>

            <div className="relative aspect-video rounded-lg overflow-hidden mb-8 bg-background-200">
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-foreground-900/80 to-foreground-950/90">
                <div className="w-20 h-20 rounded-full bg-primary-500/90 flex items-center justify-center mb-4 cursor-pointer hover:bg-primary-500 transition-colors">
                  <i className="ri-play-fill text-3xl text-background-50 ml-1"></i>
                </div>
                <p className="text-background-50/80 text-sm font-medium">{article.title}</p>
                <p className="text-background-50/50 text-xs mt-1">{article.episodeCount}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8 p-4 bg-background-100 rounded-lg border border-background-200/70">
              <div>
                <span className="text-xs text-foreground-400">导演</span>
                <p className="text-sm text-foreground-800 font-medium mt-0.5">{article.director}</p>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <span className="text-xs text-foreground-400">主演</span>
                <p className="text-sm text-foreground-800 font-medium mt-0.5">{article.cast.join(' / ')}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-400">地区</span>
                <p className="text-sm text-foreground-800 font-medium mt-0.5">{article.region}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-400">类型标签</span>
                <p className="text-sm text-foreground-800 font-medium mt-0.5">
                  {article.tags.map((tag) => (
                    <span key={tag} className="inline-block px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-600 text-xs mr-1 mb-1">
                      {tag}
                    </span>
                  ))}
                </p>
              </div>
              <div>
                <span className="text-xs text-foreground-400">更新时间</span>
                <p className="text-sm text-foreground-800 font-medium mt-0.5">{article.updateDate}</p>
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none mb-10 text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-8 mt-8">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden bg-background-200">
                        <div className="w-full h-full flex items-center justify-center">
                          {categoryInfo && (
                            <i className={`${categoryInfo.icon} text-3xl text-foreground-300`}></i>
                          )}
                        </div>
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-background-50 text-xs whitespace-nowrap">
                          {related.rating}
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading text-sm text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                          {related.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}